import { useTheme } from '@mui/material';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { KLYDO_YELLOW } from '../../../consts/colors';

export type OrderData = {
  date: string;
  orders: number;
};

export type OrdersGraphProps = {
  data: OrderData[];
  totalOrders: number;
  isEmpty?: boolean;
  hasError?: boolean;
};

const OrdersGraph = ({
  data,
  totalOrders,
  isEmpty,
  hasError,
}: OrdersGraphProps) => {
  const { spacing, palette } = useTheme();

  const renderContent = () => {
    if (hasError) {
      return (
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: spacing(40),
          }}
        >
          <DSTypography
            variant="body1"
            sx={{ color: palette.error.main, textAlign: 'center' }}
          >
            Unable to load orders data
          </DSTypography>
        </DSBox>
      );
    }

    if (isEmpty || data.length === 0) {
      return (
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: spacing(40),
          }}
        >
          <DSTypography
            variant="body1"
            sx={{ color: palette.text.secondary, textAlign: 'center' }}
          >
            No orders data available for this period
          </DSTypography>
        </DSBox>
      );
    }

    return (
      <>
        <DSBox
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing(3),
          }}
        >
          <DSTypography variant="h6">Orders</DSTypography>
          <DSTypography variant="h6">Total {totalOrders}</DSTypography>
        </DSBox>

        <DSBox
          sx={{
            height: '85%',
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} style={{ marginLeft: -20 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255, 255, 255, 0.1)"
              />
              <XAxis
                dataKey="date"
                stroke="rgba(255, 255, 255, 0.5)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="rgba(255, 255, 255, 0.5)"
                style={{ fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  color: '#fff',
                }}
              />
              <Line
                type="monotone"
                dataKey="orders"
                stroke={KLYDO_YELLOW}
                strokeWidth={2}
                dot={{ fill: KLYDO_YELLOW, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </DSBox>
      </>
    );
  };

  return (
    <DSBox
      sx={{
        backgroundColor: palette.paper.elevation5,
        padding: spacing(4),
        height: '100%',
        borderRadius: spacing(2),
        minHeight: spacing(40),
      }}
    >
      {renderContent()}
    </DSBox>
  );
};

export default OrdersGraph;
